import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { WidgetProps, WidgetRef } from '../type';
import { ChatMessageData, SystemMessageData } from '@/types';
import { sleep } from '@/lib/utils';

interface ConfettiPiece {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  rotation: number;
  rotationSpeed: number;
  color: string;
  size: number;
  alpha: number;
  update: () => void;
  draw: (ctx: CanvasRenderingContext2D) => void;
};

const colors = ['#ff0a54', '#ff477e', '#ff7096', '#ff85a1', '#fbb1bd', '#f9bec7', '#f7cad0'];

const playTadaSound = (ctx: AudioContext, variant: "one" | "two") => {
  const now = ctx.currentTime;

  const playNote = (
    freq: number,
    delay: number,
    duration: number = 0.4,
    type: OscillatorType = 'triangle',
    volume: number = 0.12
  ) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, now + delay);

    gain.gain.setValueAtTime(volume, now + delay);
    gain.gain.exponentialRampToValueAtTime(0.001, now + delay + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now + delay);
    osc.stop(now + delay + duration + 0.1);
  };

  const playNoiseBurst = (delay: number, duration = 0.3, volume = 0.2) => {
    const bufferSize = ctx.sampleRate * duration;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = (Math.random() * 2 - 1) * 0.5; // white noise
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(volume, now + delay);
    gain.gain.exponentialRampToValueAtTime(0.001, now + delay + duration);

    noise.connect(gain);
    gain.connect(ctx.destination);

    noise.start(now + delay);
    noise.stop(now + delay + duration + 0.1);
  };

  if (variant == "one") {
    playNote(523.25, 0.00);
    playNote(659.25, 0.10);
    playNote(783.99, 0.20);

    playNote(523.25, 0.30);
    playNote(659.25, 0.40);
    playNote(1046.5, 0.60);
    playNote(1318.5, 0.75, 0.3, 'sine', 0.05);

    playNoiseBurst(0.1, 0.9, 0.7);
    playNoiseBurst(0.3);
    playNoiseBurst(0.8, 0.2);
    Math.random() < 0.4 && playNoiseBurst(1.0, 0.6);
    playNoiseBurst(1.2, 0.4);
    Math.random() < 0.6 && playNoiseBurst(2, 0.5);
  } else {
    playNote(523.25, 0.0);
    playNote(659.25, 0.1);
    playNote(783.99, 0.2);

    playNote(1046.50, 0.5);
    playNote(1318.51, 0.6);
    playNote(1567.98, 0.7);

    playNoiseBurst(0.8, 0.2);
    Math.random() < 0.7 && playNoiseBurst(1.0, 0.6);
    playNoiseBurst(1.2, 0.4);
    Math.random() < 0.6 && playNoiseBurst(2, 0.5);
  }

};

const ConfettiCanvas = forwardRef<WidgetRef, WidgetProps>(({ targetRef, isFullscreen }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const piecesRef = useRef<ConfettiPiece[]>([]);
  const dimensions = useRef({ width: window.innerWidth, height: window.innerHeight });
  const audioCtxRef = useRef<AudioContext | null>(null);

  const animationFrameRef = useRef<number | null>(null);
  const isAnimatingRef = useRef(false);

  const playSound = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    playTadaSound(audioCtxRef.current,
      Math.random() < 0.5 ? "one" : "two");
  };

  const createConfetti = (): ConfettiPiece => {
    const size = Math.random() * 8 + 4;
    const x = Math.random() * window.innerWidth;
    const y = -20;
    const velocityX = (Math.random() - 0.5) * 5;
    const velocityY = Math.random() * 3 + 5;
    const rotation = Math.random() * Math.PI * 2;
    const rotationSpeed = (Math.random() - 0.5) * 0.2;
    const color = colors[Math.floor(Math.random() * colors.length)];
    const alpha = 1;

    return {
      x,
      y,
      velocityX,
      velocityY,
      rotation,
      rotationSpeed,
      color,
      size,
      alpha,
      update() {
        this.x += this.velocityX;
        this.y += this.velocityY;
        this.rotation += this.rotationSpeed;
        this.alpha -= 0.003;
      },
      draw(ctx) {
        ctx.save();
        ctx.globalAlpha = this.alpha;
        ctx.fillStyle = this.color;
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        ctx.fillRect(-this.size / 2, -this.size / 2, this.size, this.size);
        ctx.restore();
      }
    };
  };

  const startAnimation = () => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;

    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      if (!isAnimatingRef.current) return;
      animationFrameRef.current = requestAnimationFrame(animate);
      const { width, height } = dimensions.current;

      ctx.clearRect(0, 0, width, height);

      piecesRef.current.forEach(p => {
        p.update();
        p.draw(ctx);
      });

      piecesRef.current = piecesRef.current.filter(p => p.alpha > 0 && p.y < height + 50);

      if (piecesRef.current.length === 0) {
        ctx.clearRect(0, 0, width, height);
        stopAnimation();
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  };

  const stopAnimation = () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    isAnimatingRef.current = false;
  };

  const getBounds = () => {
    const container = targetRef?.current;
    return isFullscreen
      ? { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }
      : container?.getBoundingClientRect() ?? { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
  };

  const resize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    dimensions.current = getBounds();
    canvas.width = dimensions.current.width;
    canvas.height = dimensions.current.height;
  };

  useImperativeHandle(ref, () => ({
    onMessage: (options: { message: SystemMessageData | ChatMessageData }) => {
      function extractNumber(str: string) {
        const match = str.match(/\d+/);
        return match ? parseInt(match[0], 10) : 100;
      }

      let num = extractNumber(options.message.message);
      if (num < 10 || num > 200) {
        num = Math.floor(Math.random() * 50) + 50;
      }

      async function start() {
        for (let i = 0; i < num; i++) {
          await sleep(Math.random() * 50);
          piecesRef.current.push(createConfetti());
          startAnimation();
        }
      }
      playSound();
      setTimeout(start, 300);
    }
  }));

  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);
    return () => {
      stopAnimation();
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={
        `pointer-events-none z-50 bg-transparent ${isFullscreen
          ? 'fixed top-0 left-0 w-screen h-screen'
          : 'absolute top-0 left-0 w-full h-full'}`
      }
    />
  );
});

export default ConfettiCanvas;
